import React, { useState, useEffect } from 'react';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Header from '../../components/ui/Header';
import { aiApiConfigService } from '../../services/aiApiConfigService';

const AIApiConfigurationCenter = () => {
  const [config, setConfig] = useState({
    openai: { apiKey: '', enabled: false, model: 'gpt-4o-mini', usageLimit: 1000 },
    gemini: { apiKey: '', enabled: false, model: 'gemini-2.0-flash-exp', usageLimit: 1000 },
    anthropic: { apiKey: '', enabled: false, model: 'claude-3-haiku-20240307', usageLimit: 1000 }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState({});
  const [testResults, setTestResults] = useState({});
  const [showKeys, setShowKeys] = useState({});
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const providers = [
    {
      id: 'openai',
      name: 'OpenAI',
      icon: 'Sparkles',
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      description: 'GPT-4, GPT-3.5 Turbo e modelos de embeddings',
      models: [
        { value: 'gpt-4o', label: 'GPT-4o (Mais recente)' },
        { value: 'gpt-4o-mini', label: 'GPT-4o Mini (Econômico)' },
        { value: 'gpt-4-turbo', label: 'GPT-4 Turbo' },
        { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo' }
      ],
      docsUrl: 'https://platform.openai.com/api-keys'
    },
    {
      id: 'gemini',
      name: 'Google Gemini',
      icon: 'Zap',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      description: 'Gemini Pro, Flash e modelos multimodais',
      models: [
        { value: 'gemini-2.0-flash-exp', label: 'Gemini 2.0 Flash (Gratuito)' },
        { value: 'gemini-1.5-pro', label: 'Gemini 1.5 Pro' },
        { value: 'gemini-1.5-flash', label: 'Gemini 1.5 Flash' }
      ],
      docsUrl: 'https://aistudio.google.com/app/apikey'
    },
    {
      id: 'anthropic',
      name: 'Anthropic Claude',
      icon: 'Brain',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      description: 'Claude 3 Opus, Sonnet e Haiku',
      models: [
        { value: 'claude-3-opus-20240229', label: 'Claude 3 Opus' },
        { value: 'claude-3-sonnet-20240229', label: 'Claude 3 Sonnet' },
        { value: 'claude-3-haiku-20240307', label: 'Claude 3 Haiku' }
      ],
      docsUrl: 'https://console.anthropic.com/settings/keys'
    }
  ];

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      setError('');
      const savedConfig = await aiApiConfigService?.getConfig();
      
      if (savedConfig && Object.keys(savedConfig)?.length > 0) {
        setConfig(prevConfig => ({
          openai: { ...prevConfig?.openai, ...savedConfig?.openai },
          gemini: { ...prevConfig?.gemini, ...savedConfig?.gemini },
          anthropic: { ...prevConfig?.anthropic, ...savedConfig?.anthropic }
        }));
      }
    } catch (err) {
      console.error('Error loading config:', err);
      setError(err?.message || 'Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccessMessage('');
      
      await aiApiConfigService?.updateConfig(config);
      setSuccessMessage('Configurações salvas com sucesso!');
      
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error saving config:', err);
      setError(err?.message || 'Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async (providerId) => {
    const apiKey = config?.[providerId]?.apiKey;
    
    if (!apiKey) {
      setTestResults(prev => ({
        ...prev,
        [providerId]: { success: false, message: 'Chave API não configurada' }
      }));
      return;
    }

    try {
      setTesting(prev => ({ ...prev, [providerId]: true }));
      setTestResults(prev => ({ ...prev, [providerId]: null }));
      
      const result = await aiApiConfigService?.testConnection(providerId, apiKey);
      setTestResults(prev => ({ ...prev, [providerId]: result }));
    } catch (err) {
      console.error(`Error testing ${providerId}:`, err);
      setTestResults(prev => ({
        ...prev,
        [providerId]: { success: false, message: err?.message || 'Erro ao testar conexão' }
      }));
    } finally {
      setTesting(prev => ({ ...prev, [providerId]: false }));
    }
  };

  const updateProviderConfig = (providerId, field, value) => {
    setConfig(prev => ({
      ...prev,
      [providerId]: {
        ...prev?.[providerId],
        [field]: value
      }
    }));
    
    // Clear test results when API key changes
    if (field === 'apiKey') {
      setTestResults(prev => ({ ...prev, [providerId]: null }));
    }
  };

  const toggleShowKey = (providerId) => {
    setShowKeys(prev => ({ ...prev, [providerId]: !prev?.[providerId] }));
  };

  const getDisplayKey = (providerId) => {
    const apiKey = config?.[providerId]?.apiKey;
    if (!apiKey) return '';
    if (showKeys?.[providerId]) return apiKey;
    return aiApiConfigService?.maskApiKey(apiKey);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16 flex items-center justify-center h-screen">
          <div className="flex items-center gap-3 text-muted-foreground">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <span>Carregando configurações...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <Icon name="Key" className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-foreground">
                  Central de Configuração de IA
                </h1>
                <p className="text-muted-foreground mt-1">
                  Gerencie suas chaves API para múltiplos provedores de IA
                </p>
              </div>
            </div>
          </div>

          {/* Error/Success Messages */}
          {error && (
            <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-3">
              <Icon name="AlertCircle" className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {successMessage && (
            <div className="mb-6 p-4 bg-success/10 border border-success/20 rounded-lg flex items-start gap-3">
              <Icon name="CheckCircle" className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
              <p className="text-sm text-success">{successMessage}</p>
            </div>
          )}

          {/* Security Notice */}
          <div className="mb-8 p-6 bg-card border border-border rounded-lg">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <Icon name="Shield" className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  Segurança e Privacidade
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Suas chaves API são armazenadas de forma segura e criptografada no banco de dados. 
                  Nunca compartilhamos suas credenciais com terceiros. As chaves são usadas apenas 
                  para realizar chamadas diretas aos provedores de IA em seu nome.
                </p>
              </div>
            </div>
          </div>

          {/* Provider Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {providers?.map((provider) => {
              const providerConfig = config?.[provider?.id];
              const isEnabled = providerConfig?.enabled;
              const testResult = testResults?.[provider?.id];
              const isTesting = testing?.[provider?.id];

              return (
                <div
                  key={provider?.id}
                  className="bg-card border border-border rounded-lg p-6 transition-all hover:shadow-lg"
                >
                  {/* Provider Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-lg ${provider?.bgColor} flex items-center justify-center`}>
                        <Icon name={provider?.icon} className={`w-6 h-6 ${provider?.color}`} />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-foreground">
                          {provider?.name}
                        </h3>
                        <p className="text-xs text-muted-foreground">
                          {provider?.description}
                        </p>
                      </div>
                    </div>
                    
                    {/* Enable Toggle */}
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isEnabled}
                        onChange={(e) => updateProviderConfig(provider?.id, 'enabled', e?.target?.checked)}
                        className="w-4 h-4 rounded border-input text-primary focus:ring-2 focus:ring-primary"
                      />
                      <span className="text-sm text-muted-foreground">Ativo</span>
                    </label>
                  </div>
                  {/* API Key Input */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Chave API
                      <span className="text-destructive ml-1">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showKeys?.[provider?.id] ? 'text' : 'password'}
                        value={providerConfig?.apiKey || ''}
                        onChange={(e) => updateProviderConfig(provider?.id, 'apiKey', e?.target?.value)}
                        placeholder="sk-..." 
                        className="w-full h-10 px-3 pr-10 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                      <button
                        type="button"
                        onClick={() => toggleShowKey(provider?.id)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-muted rounded"
                      >
                        <Icon 
                          name={showKeys?.[provider?.id] ? 'EyeOff' : 'Eye'} 
                          className="w-4 h-4 text-muted-foreground" 
                        />
                      </button>
                    </div>
                    <a
                      href={provider?.docsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-1"
                    >
                      <Icon name="ExternalLink" className="w-3 h-3" />
                      Obter chave API
                    </a>
                  </div>
                  {/* Model Selection */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Modelo Padrão
                    </label>
                    <select
                      value={providerConfig?.model || ''}
                      onChange={(e) => updateProviderConfig(provider?.id, 'model', e?.target?.value)}
                      className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      {provider?.models?.map((model) => (
                        <option key={model?.value} value={model?.value}>
                          {model?.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  {/* Usage Limit */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Limite de Uso Mensal (R$)
                    </label>
                    <input
                      type="number"
                      value={providerConfig?.usageLimit || 0}
                      onChange={(e) => updateProviderConfig(provider?.id, 'usageLimit', parseFloat(e?.target?.value) || 0)}
                      min="0"
                      step="100"
                      className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  {/* Test Connection Button */}
                  <Button
                    onClick={() => handleTestConnection(provider?.id)}
                    disabled={!providerConfig?.apiKey || isTesting}
                    loading={isTesting}
                    variant="outline"
                    size="sm"
                    fullWidth
                    iconName="Wifi"
                    className="mb-3"
                  >
                    {isTesting ? 'Testando...' : 'Testar Conexão'}
                  </Button>
                  {/* Test Result */}
                  {testResult && (
                    <div
                      className={`p-3 rounded-md flex items-start gap-2 text-sm ${
                        testResult?.success
                          ? 'bg-success/10 border border-success/20 text-success' :'bg-destructive/10 border border-destructive/20 text-destructive'
                      }`}
                    >
                      <Icon
                        name={testResult?.success ? 'CheckCircle' : 'XCircle'}
                        className="w-4 h-4 flex-shrink-0 mt-0.5"
                      />
                      <div>
                        <p className="font-medium">{testResult?.message}</p>
                        {testResult?.success && testResult?.responseTime && (
                          <p className="text-xs opacity-80 mt-1">
                            Tempo de resposta: {testResult?.responseTime}ms
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Save Button */}
          <div className="flex justify-end gap-3">
            <Button
              onClick={loadConfig}
              variant="outline"
              disabled={saving}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              loading={saving}
              iconName="Save"
            >
              Salvar Configurações
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIApiConfigurationCenter;